import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  IconButton,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterIcon,
  Info as InfoIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import fileService from '../../services/fileService';
import { useSnackbar } from 'notistack';

interface MetadataField {
  key: string;
  value: string | number | boolean | string[] | null;
  type: 'text' | 'number' | 'boolean' | 'date' | 'tags' | 'json';
}

interface AdvancedMetadataEditorProps {
  fileId: string;
  fileName?: string;
  metadata?: Record<string, any>;
  readOnly?: boolean;
  onChange?: (metadata: Record<string, any>) => void;
}

const AdvancedMetadataEditor: React.FC<AdvancedMetadataEditorProps> = ({
  fileId,
  fileName,
  metadata = {},
  readOnly = false,
  onChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadataFields, setMetadataFields] = useState<MetadataField[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentField, setCurrentField] = useState<MetadataField | null>(null);
  const [tagsInput, setTagsInput] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Process the metadata object to array format for easier rendering and editing
  useEffect(() => {
    const fields: MetadataField[] = [];
    Object.entries(metadata).forEach(([key, value]) => {
      // Determine field type based on value
      let type: MetadataField['type'] = 'text';
      
      if (Array.isArray(value)) {
        type = 'tags';
      } else if (typeof value === 'number') {
        type = 'number';
      } else if (typeof value === 'boolean') {
        type = 'boolean';
      } else if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
        type = 'date';
      } else if (value !== null && typeof value === 'object') {
        type = 'json';
        value = JSON.stringify(value, null, 2);
      }
      
      fields.push({
        key,
        value,
        type,
      });
    });
    
    setMetadataFields(fields);
  }, [metadata]);

  const handleSaveMetadata = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Convert metadata fields back to object
      const metadataObj: Record<string, any> = {};
      metadataFields.forEach(field => {
        let value = field.value;
        
        // Convert value based on type
        if (field.type === 'number' && typeof value === 'string') {
          value = parseFloat(value);
        } else if (field.type === 'date' && typeof value === 'string') {
          value = new Date(value).toISOString();
        } else if (field.type === 'json' && typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (err) {
            throw new Error(`Invalid JSON for field "${field.key}"`);
          }
        }
        
        metadataObj[field.key] = value;
      });
      
      await fileService.updateFileMetadata(fileId, metadataObj);
      
      enqueueSnackbar('Metadata updated successfully', { variant: 'success' });
      
      if (onChange) {
        onChange(metadataObj);
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to update metadata');
      enqueueSnackbar('Failed to update metadata', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = () => {
    setCurrentField({
      key: '',
      value: '',
      type: 'text',
    });
    setEditDialogOpen(true);
  };

  const handleEditField = (field: MetadataField) => {
    setCurrentField({ ...field });
    
    if (field.type === 'tags' && Array.isArray(field.value)) {
      setTagsInput(field.value.join(', '));
    }
    
    setEditDialogOpen(true);
  };

  const handleDeleteField = (key: string) => {
    if (window.confirm(`Are you sure you want to delete the "${key}" metadata field?`)) {
      setMetadataFields(metadataFields.filter(field => field.key !== key));
    }
  };

  const handleSaveField = () => {
    if (!currentField) return;
    
    if (!currentField.key.trim()) {
      setError('Field name cannot be empty');
      return;
    }
    
    let fieldValue = currentField.value;
    
    // Process tags
    if (currentField.type === 'tags') {
      fieldValue = tagsInput.split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
    }
    
    // Check if field exists
    const existingIndex = metadataFields.findIndex(field => field.key === currentField.key);
    
    if (existingIndex >= 0) {
      // Update existing field
      const updatedFields = [...metadataFields];
      updatedFields[existingIndex] = {
        ...currentField,
        value: fieldValue,
      };
      setMetadataFields(updatedFields);
    } else {
      // Add new field
      setMetadataFields([
        ...metadataFields,
        {
          ...currentField,
          value: fieldValue,
        },
      ]);
    }
    
    setEditDialogOpen(false);
    setCurrentField(null);
    setTagsInput('');
    setError(null);
  };

  const renderFieldValue = (field: MetadataField) => {
    const { type, value } = field;
    
    if (value === null || value === undefined) {
      return <Typography color="text.secondary">No value</Typography>;
    }
    
    switch (type) {
      case 'tags':
        if (!Array.isArray(value) || value.length === 0) {
          return <Typography color="text.secondary">No tags</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {value.map((tag, index) => (
              <Chip 
                key={index} 
                label={tag} 
                size="small" 
                icon={<LabelIcon />}
              />
            ))}
          </Box>
        );
      
      case 'boolean':
        return (
          <Chip 
            label={value ? 'True' : 'False'} 
            color={value ? 'success' : 'default'} 
            size="small" 
          />
        );
      
      case 'date':
        try {
          const date = new Date(value as string);
          return date.toLocaleString();
        } catch (err) {
          return String(value);
        }
      
      case 'json':
        return (
          <Typography 
            component="pre" 
            sx={{ 
              whiteSpace: 'pre-wrap', 
              backgroundColor: 'background.paper', 
              p: 1, 
              borderRadius: 1,
              fontSize: 'small'
            }}
          >
            {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          </Typography>
        );
      
      default:
        return String(value);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Advanced Metadata
        </Typography>
        <Tooltip title="About metadata">
          <IconButton 
            size="small" 
            onClick={() => setInfoDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Paper sx={{ mb: 3 }}>
        {metadataFields.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No metadata fields available for this file.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {metadataFields.map((field, index) => (
              <React.Fragment key={field.key}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    !readOnly && (
                      <Box>
                        <Tooltip title="Edit field">
                          <IconButton 
                            edge="end" 
                            aria-label="edit"
                            onClick={() => handleEditField(field)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete field">
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => handleDeleteField(field.key)}
                            size="small"
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  }
                >
                  <ListItemText
                    primary={
                      <Typography 
                        component="span" 
                        variant="subtitle2" 
                        color="primary"
                      >
                        {field.key}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={field.type} 
                          size="small" 
                          variant="outlined"
                          sx={{ mr: 1, fontSize: '0.7rem' }}
                        />
                        {renderFieldValue(field)}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddField}
            variant="outlined"
          >
            Add Field
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSaveMetadata}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
      
      {/* Edit Metadata Field Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setCurrentField(null);
          setTagsInput('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentField?.key ? 'Edit Metadata Field' : 'Add Metadata Field'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Field Name"
                fullWidth
                value={currentField?.key || ''}
                onChange={(e) => setCurrentField(prev => prev ? { ...prev, key: e.target.value } : null)}
                disabled={Boolean(currentField?.key) && !currentField?.key.startsWith('custom.')}
                helperText={Boolean(currentField?.key) && !currentField?.key.startsWith('custom.') ? 
                  "System field names cannot be changed" : 
                  "Use 'custom.' prefix for your own fields"
                }
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={currentField?.type || 'text'}
                  onChange={(e) => setCurrentField(prev => prev ? 
                    { ...prev, type: e.target.value as MetadataField['type'] } : 
                    null
                  )}
                  label="Field Type"
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="boolean">Boolean</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="tags">Tags</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              {currentField?.type === 'boolean' ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(currentField.value)}
                      onChange={(e) => setCurrentField(prev => prev ? 
                        { ...prev, value: e.target.checked } : 
                        null
                      )}
                    />
                  }
                  label="Value"
                />
              ) : currentField?.type === 'tags' ? (
                <TextField
                  label="Tags (comma-separated)"
                  fullWidth
                  multiline
                  rows={3}
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  helperText="Enter tags separated by commas"
                />
              ) : currentField?.type === 'json' ? (
                <TextField
                  label="JSON Value"
                  fullWidth
                  multiline
                  rows={5}
                  value={currentField.value as string || ''}
                  onChange={(e) => setCurrentField(prev => prev ? 
                    { ...prev, value: e.target.value } : 
                    null
                  )}
                  placeholder="{}"
                  helperText="Enter valid JSON data"
                />
              ) : (
                <TextField
                  label="Field Value"
                  fullWidth
                  type={currentField?.type === 'number' ? 'number' : 
                         currentField?.type === 'date' ? 'datetime-local' : 'text'}
                  value={currentField?.value || ''}
                  onChange={(e) => setCurrentField(prev => prev ? 
                    { ...prev, value: e.target.value } : 
                    null
                  )}
                  multiline={currentField?.type === 'text'}
                  rows={currentField?.type === 'text' ? 3 : 1}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setCurrentField(null);
              setTagsInput('');
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveField}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          About Advanced Metadata
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Advanced metadata allows you to store and organize additional information about your files.
          </Typography>
          
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Common metadata fields:
          </Typography>
          <ul>
            <li><strong>custom.category</strong> - Categorize files by project, department, etc.</li>
            <li><strong>custom.status</strong> - Track file status (draft, review, final, etc.)</li>
            <li><strong>custom.dueDate</strong> - Set deadline for file-related tasks</li>
            <li><strong>custom.tags</strong> - Add searchable keywords to files</li>
            <li><strong>custom.author</strong> - Track the original content creator</li>
          </ul>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Use the 'custom.' prefix for your custom fields to avoid conflicts with system metadata.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedMetadataEditor; 